<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>Pré-Matrículas</h1>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="searchQuery"
          label="Pesquisar por CPF, Nome, Turma ou Série"
          dense
          solo
          clearable
          @input="pesquisar"
          prepend-inner-icon="mdi-magnify"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="prematriculas"
              :loading="loading"
              no-data-text="Nenhuma pré-matrícula encontrada"
              class="elevation-1"
            >
              <template v-slot:item.cpf="{ item }">
                {{ item.cpf }}
              </template>

              <template v-slot:item.nome="{ item }">
                {{ item.nome || (item.aluno ? item.aluno.nomecompleto : "") }}
              </template>

              <template v-slot:item.configuracao="{ item }">
                {{ item.configuracao ? item.configuracao.descricao : "" }}
              </template>

              <template v-slot:item.turma="{ item }">
                {{ item.turma ? item.turma.descricao : "" }}
              </template>

              <template v-slot:item.serie="{ item }">
                {{ item.serie ? item.serie.descricao : "" }}
              </template>

              <template v-slot:[`item.tipo_atendimento`]="{ item }">
                <v-chip x-small :color="getChipColor(item)" dark>
                  {{ getTipoLabel(item) }}
                </v-chip>
              </template>

              <template v-slot:item.filiacao_1="{ item }">
                {{ item.filiacao_1 || (item.aluno ? item.aluno.filiacao1 : "") }}
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="item.status === 'pendente' ? 'warning' : 'success'"
                  small
                  text-color="white"
                >
                  {{ item.status === "pendente" ? "Pendente" : "Matriculado" }}
                </v-chip>
              </template>

              <template v-slot:item.acoes="{ item }">
                <v-btn
                  v-if="item.status === 'pendente'"
                  color="primary"
                  small
                  text
                  @click="abrirModalMatricular(item)"
                >
                  Matricular
                </v-btn>

                <v-btn
                  v-if="item.status === 'pendente'"
                  color="error"
                  small
                  text
                  @click="confirmarExclusao(item)"
                >
                  Excluir
                </v-btn>

                <span v-if="item.status === 'matriculado'" class="grey--text">
                  Matrícula já concluída
                </span>
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-actions v-if="pagination.lastPage > 1">
            <v-spacer></v-spacer>
            <v-pagination
              v-model="pagination.currentPage"
              :length="pagination.lastPage"
              :total-visible="7"
              @input="mudarPagina"
            ></v-pagination>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <modal-matricular
      v-if="modalMatricular"
      :prematricula="prematriculaSelecionada"
      @close="fecharModalMatricular"
      @sucesso="listarPrematriculas"
    />

    <v-dialog v-model="dialogExcluir" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirmar Exclusão</v-card-title>
        <v-card-text> Tem certeza que deseja excluir esta pré-matrícula? </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogExcluir = false">Cancelar</v-btn>
          <v-btn color="error" text @click="excluir" :loading="loading">Excluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
import ModalMatricular from "./components/ModalMatricular.vue";

export default {
  name: "PreMatriculasIndexPage",
  components: {
    ModalMatricular,
  },
  data() {
    return {
      prematriculas: [],
      searchQuery: "",
      loading: false,
      modalMatricular: false,
      dialogExcluir: false,
      prematriculaSelecionada: null,
      pagination: {
        currentPage: 1,
        perPage: 15,
        total: 0,
        lastPage: 1,
      },
      headers: [
        { text: "CPF", value: "cpf" },
        { text: "Nome do Aluno", value: "nome" },
        { text: "Escola", value: "configuracao" },
        { text: "Turma", value: "turma" },
        { text: "Série", value: "serie" },
        { text: "Tipo", value: "tipo_atendimento" },
        { text: "Filiação 1", value: "filiacao_1" },
        { text: "Status", value: "status" },
        { text: "Ações", value: "acoes", sortable: false },
      ],
    };
  },
  methods: {
    isAEE(prematricula) {
      if (!prematricula || !prematricula.turma) return false;
      return (
        prematricula.turma.tipo_de_atendimento === "Atendimento educacional especializado (AEE)"
      );
    },
    isAtividadeComplementar(prematricula) {
      if (!prematricula || !prematricula.turma) return false;
      return prematricula.turma.tipo_de_atendimento === "Atividade complementar";
    },
    getTipoLabel(prematricula) {
      if (this.isAEE(prematricula)) return "AEE";
      if (this.isAtividadeComplementar(prematricula)) return "Ativ. Compl.";
      return "Regular";
    },
    getChipColor(prematricula) {
      if (this.isAEE(prematricula)) return "purple";
      if (this.isAtividadeComplementar(prematricula)) return "green";
      return "blue";
    },
    async listarPrematriculas() {
      this.loading = true;
      try {
        const result = await this.$services.preMatriculasService.listar(
          this.searchQuery,
          this.pagination.currentPage,
          this.pagination.perPage
        );
        this.prematriculas = result.prematriculas;
        this.pagination = result.pagination;
      } catch (error) {
        this.$toast.danger(error.message || "Erro ao carregar pré-matrículas");
      } finally {
        this.loading = false;
      }
    },
    mudarPagina(page) {
      this.pagination.currentPage = page;
      this.listarPrematriculas();
    },
    pesquisar() {
      this.pagination.currentPage = 1;
      this.listarPrematriculas();
    },
    abrirModalMatricular(prematricula) {
      this.prematriculaSelecionada = prematricula;
      this.modalMatricular = true;
    },
    fecharModalMatricular() {
      this.modalMatricular = false;
      this.prematriculaSelecionada = null;
    },
    confirmarExclusao(prematricula) {
      this.prematriculaSelecionada = prematricula;
      this.dialogExcluir = true;
    },
    async excluir() {
      this.loading = true;
      try {
        await this.$services.preMatriculasService.excluir(this.prematriculaSelecionada.id);
        this.$toast.success("Pré-matrícula excluída com sucesso!");
        this.dialogExcluir = false;
        this.listarPrematriculas();
      } catch (error) {
        this.$toast.danger(error.message || "Erro ao excluir pré-matrícula");
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.listarPrematriculas();
  },
};
</script>
